import {
  type ForecastVariableKey,
  isForecastVariableKey,
} from '../../utils/WeatherMap/forecastCatalog';
import type {MapPreferencesState, ObservationLayer} from './index';

/**
 * The part of a root state these selectors read. Optional all the way down,
 * on purpose:
 * - the slice is not in `CommonRootState` (mobile does not register it yet),
 *   and app test harnesses build partial stores;
 * - `autoMergeLevel2` merges only a slice's own keys and restores anything
 *   nested exactly as stored, so a blob written before a layer existed comes
 *   back with `observations` lacking that key. Every read therefore carries
 *   its default rather than trusting `initialState` to have filled it in.
 */
export type MapPreferencesRootState = {
  mapPreferences?: {
    observations?: Partial<MapPreferencesState['observations']>;
    forecast?: Partial<MapPreferencesState['forecast']>;
  };
};

/** Every layer is shown unless the user turned it off. */
const isLayerShown = (
  state: MapPreferencesRootState,
  layer: ObservationLayer,
) => state.mapPreferences?.observations?.[layer] ?? true;

export const selectMapStationLayer = (state: MapPreferencesRootState) =>
  isLayerShown(state, 'stations');

export const selectMapRainRadarLayer = (state: MapPreferencesRootState) =>
  isLayerShown(state, 'rainRadar');

export const selectMapLightningLayer = (state: MapPreferencesRootState) =>
  isLayerShown(state, 'lightning');

/** The forecast map last picked on this device, if any. */
export const selectSavedForecastConfigId = (
  state: MapPreferencesRootState,
): number | null => state.mapPreferences?.forecast?.configId ?? null;

/** The wind-particle animation: on unless the user turned it off. */
export const selectForecastWindParticles = (state: MapPreferencesRootState) =>
  state.mapPreferences?.forecast?.windParticles ?? true;

/**
 * The rail variable forecast mode opens on when the URL names none (#327
 * MR5d): the last one chosen, else temperature (Auto on temperature is the
 * default). A stored value that is no rail variable reads as the default.
 */
export const selectForecastVariable = (
  state: MapPreferencesRootState,
): ForecastVariableKey => {
  const stored = state.mapPreferences?.forecast?.variable;
  return isForecastVariableKey(stored) ? stored : 'temperature';
};
